/**
 * Filter Bar Component
 * Combines search and filter controls for list pages
 */

import React from "react";
import {
  Box,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { SearchFilter } from "./SearchFilter";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterBarProps {
  /**
   * Called when the search value changes. Omit to hide the search box
   * entirely — do NOT pass a no-op, which renders a search input that
   * silently does nothing.
   */
  onSearchChange?: (value: string) => void;
  /**
   * Controlled search value. Pass the persisted search term (e.g. from
   * useUrlFilters) so the input is pre-filled when the user returns to a
   * filtered list — without it the data is filtered but the box looks empty.
   */
  searchValue?: string;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Status filter value */
  statusValue?: string;
  /** Called when status filter changes */
  onStatusChange?: (value: string) => void;
  /** Status filter options (first option should be "All" with empty value) */
  statusOptions?: FilterOption[];
  /** Status filter label */
  statusLabel?: string;
  /** Additional filter controls */
  additionalFilters?: React.ReactNode;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  onSearchChange,
  searchValue,
  searchPlaceholder = "Search...",
  statusValue,
  onStatusChange,
  statusOptions,
  statusLabel = "Status",
  additionalFilters,
}) => {
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    onStatusChange?.(event.target.value);
  };

  return (
    <Card sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {onSearchChange && (
          <SearchFilter
            placeholder={searchPlaceholder}
            value={searchValue}
            onSearch={onSearchChange}
            fullWidth
            ariaLabel={searchPlaceholder}
          />
        )}
        {statusOptions && onStatusChange && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="status-filter-label">{statusLabel}</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusValue || ""}
              onChange={handleStatusChange}
              label={statusLabel}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {additionalFilters}
      </Box>
    </Card>
  );
};

export default FilterBar;
